/**
 * What the ad groups section says, as finished sentences.
 *
 * Out of the component so a runner can read it, the same arrangement as
 * `campaign-copy.ts`. Nothing here says how much may be paid out: a budget is shown as
 * TikTok reports it, in the account's currency, with no arithmetic.
 *
 * 🔴 **The section stops and does not start, and it says which.** It used to read only,
 * and the notice then said nothing here could change an ad group — true when it was
 * written and false the moment the stop control arrived, so it is rewritten rather than
 * kept. What stays is the shape of the claim: name what the screen cannot do, because an
 * operator hunting for a control that is absent reads the absence as a broken button
 * (twads#126 named the opposite failure, a control present and not working).
 */
import type { StopOutcome } from "./ad-groups.ts";
import type { AdGroup, CampaignPage } from "./api.ts";

export interface Banner {
	title: string;
	body: string;
}

/** `GET /adgroups` said `truncated`: TikTok counts more than one page holds. */
export const FLOOR_BANNER: Banner = {
	title: "These ad groups are a floor, not a total.",
	body: "TikTok counts more ad groups under this campaign than one page holds, so this list is the first page. Nothing was filtered out.",
};

/**
 * Said on the section itself, not in a tooltip.
 *
 * It names no API route now that the screen performs the stop itself. The case that
 * reads the contract stays as a constraint rather than a measurement: if this sentence
 * ever names an `/api/v1` route again, that route has to be declared. It earned its
 * keep once — an earlier draft sent the reader to `POST /api/v1/adgroups/:id/status`
 * while that route was still in an unmerged pull request, which would have been a wrong
 * direction rather than a missing one.
 */
export const STOP_ONLY =
	"This section can stop an ad group. It cannot start one: starting delivery is done in TikTok's own dashboard, on the ad account itself. A stop may be repeated safely — it asks for a state, not for a new object.";

/**
 * 🔴 Three states, where a campaign has two.
 *
 * `FROZEN` is TikTok's own word for an ad group that is terminated and cannot be
 * enabled (`adgroup/get`, portal id 1739314558673922). It is neither "on" nor "off": a
 * screen that folded it into "off" would tell an operator that something can be
 * started again, and a screen that folded it into "on" would be worse. `campaign/get`
 * documents no such value, which is why the campaign rows have two states and these
 * have three.
 */
export function switchText(operationStatus: string): string {
	if (operationStatus === "ENABLE") return "On";
	if (operationStatus === "DISABLE") return "Off";
	if (operationStatus === "FROZEN") return "Terminated — cannot be started again";
	// Not a fallback for tidiness: TikTok may add a value, and showing it verbatim is
	// the only honest answer. Folding an unknown value into "off" would be a guess
	// rendered as a fact.
	return `TikTok reports ${operationStatus}`;
}

export const NOT_REPORTED = "Not reported";

/** What TikTok is actually doing with the ad group; null when TikTok does not say. */
export function deliveryText(secondaryStatus: string | null): string {
	return secondaryStatus ?? NOT_REPORTED;
}

/**
 * The budget as TikTok reports it, with the mode beside it and no arithmetic.
 *
 * 🔴 **Only `BUDGET_MODE_INFINITE` is named as the campaign's budget, and a zero is
 * reported verbatim.** The page documents one direction — under Campaign Budget
 * Optimization `budget_mode` returns `BUDGET_MODE_INFINITE` and `budget` returns `0.0`
 * (`adgroup-get.md` lines 228 and 229). It does not document the converse, and the
 * earlier version of this function asserted it: `BUDGET_MODE_DAY` with a budget of `0`
 * was labelled "Set at the campaign (Campaign Budget Optimization)". That was a guess
 * rendered as a fact, and one the page's own logic contradicts — under CBO the mode
 * would have been `INFINITE`, not `DAY` (d6, ttads#109).
 *
 * Two further things that version got wrong, both worth keeping written down:
 *
 *   - its subject was a guess too. "Not set on the ad group" — but the budget **is**
 *     set, and set to zero; "not set" is a claim about intent where only a number is
 *     visible.
 *   - a shared sentence made two different facts look like one, so a reader would take
 *     the cause to be the same. `INFINITE` is the documented shape of CBO; `DAY + 0` is
 *     a case the page does not explain.
 *
 * So a zero falls through to the plain report, and `switchText` below is the precedent:
 * an unrecognised value is shown as TikTok sent it. That rule was already written one
 * function away and not applied here, which is the whole of this correction.
 *
 * `0 (BUDGET_MODE_DAY)` carries the mode beside the number, so it is not a lone zero
 * reading as "nothing can be spent" — the objection that produced the wrong label is
 * answered by the format rather than by a sentence.
 */
export function budgetText(g: Pick<AdGroup, "budgetMode" | "budget">): string {
	if (g.budgetMode === "BUDGET_MODE_INFINITE") {
		return "Not set on the ad group — the campaign carries it (Campaign Budget Optimization)";
	}
	return `${g.budget} (${g.budgetMode})`;
}

/** Which page of how many, in words rather than two numbers side by side. */
export function pagerLabel(p: CampaignPage): string {
	return `Page ${p.page} of ${p.totalPages} · ${p.totalNumber} ad groups TikTok counts`;
}

/**
 * Nothing to show, and the two reasons are different answers.
 *
 * "No ad groups" is a fact about the campaign. "Not read" is a fact about this screen.
 * One tells an operator to create one; the other tells them to pick a campaign. A
 * single empty state covering both would answer the wrong question half the time.
 */
export function emptyText(state: "none" | "unasked"): string {
	return state === "none"
		? "This campaign has no ad groups."
		: "Pick a campaign to read its ad groups.";
}

/**
 * What the screen says after a stop, one sentence per outcome.
 *
 * 🔴 Four, not three: `terminated` is its own answer. The operator's goal is met —
 * nothing terminated is delivering — and the reported status will never be the one asked
 * for, so neither "done" nor "it disagreed" is the truth. `lib/ad-groups.ts` keeps the
 * outcomes apart; these keep the words apart.
 */
export function stopBanner(o: StopOutcome): Banner {
	switch (o.kind) {
		case "set":
			return {
				title: "Stopped.",
				body: `TikTok reports the ad group ${o.operationStatus}. Delivery: ${deliveryText(o.secondaryStatus)}.`,
			};
		case "terminated":
			return {
				title: "Terminated — it is not delivering, and the status will not change.",
				body: `${o.error} Nothing is being spent on it; pressing stop again cannot make TikTok report DISABLE.`,
			};
		case "mismatch":
			return {
				title: "TikTok does not report it stopped.",
				body: `${o.error} The write was accepted and the read-back disagrees. A stop may be repeated safely — it asks for a state, not for a new object.`,
			};
		case "refused":
			return { title: "Not stopped.", body: o.error };
	}
}
